import { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Grid } from "@mui/material";
import axios from "axios";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { toast } from "react-toastify";
import { LocaleContext } from "../../../routing/LangRouter.js";

//import constants
import BASE_URL from "../../../constants/baseUrls.js";
import { IS_MOBILE } from "../../../constants/breakpoints.js";

//import utils
import { calculateDateDifferenceInDays, formatPersianDate } from "../../../utils/date.js";

//import customized hooks
import useCityList from "../../../hooks/useCityList.js";
import useDateChange from "../../../hooks/useDateChange.js";
import useDebouncedInputChange from "../../../hooks/useDebouncedInputChange .js";

//import general components
import { BaseButton } from "../../common/base-button/base-button.jsx";
import RangeDatePicker from "../../common/range-date-picker/range-date-picker.jsx";
import AutoCompleteComponent from "../../common/base-select/autocomplete-component.js.jsx";
import RoomsCount from "../../common/rooms-count/rooms-count.jsx";
import BaseTextField from "../../common/base-text-field/base-text-field.jsx";

//import images and icons
import { CloseCircle, Location, SearchNormal } from "iconsax-react";

//import styles
import "./hotel-search-form.scss";

function HotelSearchForm({ dispatch, openSearchForm, setRequestFlag, setUid, isHotelInternational, hotelRoute, type }) {
    //general variables
    const { control } = useForm();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const destinationRef = useRef();
    const nationalityRef = useRef();
    const rangeDatePickerRef = useRef();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();

    //sessionStorage variables
    const domesticFlightInformation = JSON.parse(sessionStorage.getItem("domesticFlightInformation"));
    const hotelInformation = JSON.parse(sessionStorage.getItem("hotelInformation"));
    const itineraryInformation = JSON.parse(sessionStorage.getItem("itineraryInformation"));
    const { isItinerary } = JSON.parse(sessionStorage.getItem("generalInformation")) || {};

    const [showAutocompplete, setShowAutocompplete] = useState({ destination: IS_MOBILE ? false : true });
    const handleShowAutocompplete = name => {
        setShowAutocompplete({ [name]: true });
    };

    //request city list. inputs:foreign, all, flight, accommodation, bus
    const { cities, isCityLoading, getCities } = useCityList(isHotelInternational, false, false, true, false);

    //handle destiantion city change
    const handleInputChange = useDebouncedInputChange(input => {
        getCities(input);
    }, 1000);

    //call request city list on page load
    useEffect(() => {
        getCities();
    }, [location.pathname]);

    //handle departure and destination change
    const [selectedDestination, setSelectedDestination] = useState(null);
    const handleDestinationChange = value => {
        setSelectedDestination(value);
        if (IS_MOBILE) setShowAutocompplete({ destination: false });
        if (!startDate && !endDate) rangeDatePickerRef.current.focus();
        setUid(null);
    };

    useEffect(() => {
        // destination default value
        setSelectedDestination(hotelInformation?.destination || null);
        setNationality(hotelInformation?.nationality);
    }, [openSearchForm]);

    //handle hotel date change
    const initialStartDate = hotelInformation?.checkIn;
    const initialEndDate = hotelInformation?.checkOut;
    const { startDate, endDate, defaultValueDate, handleDateFormatChange } = useDateChange(initialStartDate, initialEndDate, true);
    const dateChange = (dates, _, isPersianCalendar) => {
        handleDateFormatChange(dates, isPersianCalendar);
        setUid(null);
    };

    //rooms count
    const [roomsCount, setRoomCount] = useState({ adult: "", child: "", ages: "" });
    const [originRoomsCount, setOriginRoomsCount] = useState();
    const handleRoomCountChange = (RoomsCount, all) => {
        setOriginRoomsCount(RoomsCount);
        setRoomCount(values => ({
            ...values,
            adult: RoomsCount?.map(item => item?.adult)?.join(","),
            child: RoomsCount?.map(item => item?.child)?.join(","),
            ages: RoomsCount?.map(item => (item.child === 0 ? 0 : item?.childAges.map(item => item.id)))?.join(","),
        }));
        setUid("");
    };
    //rooms count default values
    const [defaultValuesRoomsCount] = useState(hotelInformation?.originRoomsCount);

    // Add state for nationality
    const [nationality, setNationality] = useState("");

    // Add handler for nationality change
    const handleNationalityChange = value => {
        setNationality(value);
        setUid("");
    };

    //country list request
    const [countryList, setCountryList] = useState([]);
    const [countrySearchValue, setCountrySearchValue] = useState("");
    const getCountryList = async () => {
        const response = await axios.get(`${BASE_URL}/api/v2/basic/countries/`);
        setCountryList(response.data.data.list);
    };
    useEffect(() => {
        getCountryList();
    }, []);

    //format date in Persian format
    const formatDate = date => {
        return formatPersianDate(date, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: undefined,
        });
    };

    // Validation function to check room details
    const validateRoom = (room) => {
        if (room.child !== room.childAges.length) {
            toast.warning(t("Toast Messages.Child Age Input"));
            return false;
        }
        return true;
    };

    //submit form
    const submitSearchForm = event => {
        event.preventDefault();
        setUid("");
        if (!selectedDestination || !startDate || !endDate || !nationality) return toast.error(t("errorMessage2"), { toastId: "custom-id-yes" });

        const daysCount = calculateDateDifferenceInDays(startDate, endDate);
        if (daysCount > 10 || daysCount < 1) return toast.error(t("errorMessage3"), { toastId: "custom-id-yes" });
        if (
            isItinerary &&
            (moment(startDate).isBefore(domesticFlightInformation.departureDate) ||
                moment(startDate).isAfter(domesticFlightInformation.returnDate) ||
                moment(endDate).isBefore(domesticFlightInformation.departureDate) ||
                moment(endDate).isAfter(domesticFlightInformation.returnDate))
        )
            return toast.error(
                locale === "en"
                    ? "The provided information is incomplete."
                    : `بازه تاریخ انتخابی باید بین 
                    ${formatDate(domesticFlightInformation.departureDate)} تا 
                    ${formatDate(domesticFlightInformation.returnDate)} باشد.`,
                { toastId: "custom-id-yes" },
            );

        if (isItinerary) {
            itineraryInformation.itineraryGenerate = true;
            sessionStorage.setItem("itineraryInformation", JSON.stringify(itineraryInformation));
        }

        const validateRooms = originRoomsCount.map(room => {
            return validateRoom(room);
        });
        if (validateRooms.includes(false)) return;

        //set sessionStorage
        const newHotelInformation = {
            destination: selectedDestination,
            checkIn: startDate || hotelInformation?.checkIn,
            checkOut: endDate || hotelInformation?.checkOut,
            originRoomsCount: originRoomsCount || hotelInformation?.originRoomsCount,
            roomsCount: roomsCount || hotelInformation?.roomsCount,
            nationality,
            type,
        };
        sessionStorage.setItem("hotelInformation", JSON.stringify(newHotelInformation));

        searchParams.delete("action");
        setSearchParams(searchParams);

        sessionStorage.removeItem("selectedHotel");
        sessionStorage.removeItem("selectedIntRooms");
        sessionStorage.removeItem("intHotelPassengers");

        if (!hotelRoute) setRequestFlag(true);
        if (!!hotelRoute) navigate(hotelRoute);
    };

    return (
        <Grid
            component="form"
            className="hotel-search-form"
            container
            item
            xs={12}
            rowGap={2.5}
            justifyContent="space-between"
            alignItems="center"
            onSubmit={submitSearchForm}
        >
            {IS_MOBILE && (
                <Grid container item xs={12} md={4}>
                    <BaseTextField
                        label={t("city")}
                        value={locale === "en" ? selectedDestination?.english_name : selectedDestination?.name}
                        onClick={() => handleShowAutocompplete("destination")}
                        radius="10px"
                        icon={<Location size="20" color="var(--teal-color-18)" />}
                        disabled={isItinerary}
                        readOnly={true}
                    />
                </Grid>
            )}

            {showAutocompplete?.destination && (
                <Grid className="hotel-search-form__autocomplete" container item md={2.5} justifyContent="space-between" alignItems="center">
                    <Grid container item xs={IS_MOBILE ? 10.5 : 12}>
                        <AutoCompleteComponent
                            label={t("city")}
                            options={cities}
                            name="city"
                            defaultValue={selectedDestination}
                            handleChange={handleDestinationChange}
                            setInputValue={handleInputChange}
                            innerRef={destinationRef}
                            control={control}
                            loading={isCityLoading}
                            disabled={isItinerary}
                            width={350}
                            icon={<Location size="20" color="var(--teal-color-18)" />}
                            open={IS_MOBILE ? true : false}
                            type="hotel"
                        />
                    </Grid>
                    {IS_MOBILE && (
                        <Grid container item xs={1}>
                            <CloseCircle
                                color="var(--dark-color-60)"
                                onClick={() => setShowAutocompplete({ departure: false, destination: false })}
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            <Grid container item xs={12} md={4}>
                <RangeDatePicker
                    handleChange={dateChange}
                    defaultValue={defaultValueDate}
                    innerRef={rangeDatePickerRef}
                    labels={[t("checkInDate"), t("checkOutDate")]}
                    minDate={new Date().setHours(0, 0, 0, 0)}
                    range={true}
                    disableDate={isItinerary}
                />
            </Grid>

            <Grid container item xs={5.8} md={2} alignItems="stretch">
                <RoomsCount handleChange={handleRoomCountChange} defaultValue={defaultValuesRoomsCount} multiRoom={true} validation={50} />
            </Grid>

            <Grid container item xs={5.8} md={2} justifyContent="space-between">
                <AutoCompleteComponent
                    label={t("nationality")}
                    options={countryList}
                    name="nationality"
                    defaultValue={nationality}
                    handleChange={handleNationalityChange}
                    setInputValue={handleInputChange}
                    innerRef={nationalityRef}
                    control={control}
                    loading={isCityLoading}
                    disabled={false}
                    width={350}
                />
            </Grid>

            <Grid container item md={1}>
                <BaseButton
                    text={`${t("search")}`}
                    color="primary-10"
                    size="large"
                    radius="radius-20"
                    type="submit"
                    leftIcon={<SearchNormal size="24" color="var(--primary-color)" />}
                />
            </Grid>
        </Grid>
    );
}

export default connect(mapStateToProps)(HotelSearchForm);

function mapStateToProps(state) {
    return {
        hotelSort: state.hotelSort,
        hotelSelectedFilters: state.hotelSelectedFilters,
    };
}
